import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Phone, Globe, CheckCircle2, ArrowLeft, ArrowRight, Mail, ShieldCheck, PhoneOutgoing, PhoneIncoming, Clock, FileCheck, MapPin } from 'lucide-react';
import Seo, { faqLd } from '../../components/Seo';
import NotFound from '../../components/NotFound';
import { publicCoverageApi } from '../../services/publicApi';
import { useToast } from '../../hooks/use-toast';
import { flagCode } from '../../utils/flags';
import { orderUrl } from '../../utils/numberInfo';
import {
  KIND_INFO, allDocuments, countryDescription, countryFaqs, countryPath, countryTitle, dialCodeFor, kindsOf, leadTimes, listJoin,
} from '../../utils/countryPage';

const NAVY = '#0a1f2e';
const BADGE_COLOURS = {
  local: 'bg-orange-500', national: 'bg-purple-500', mobile: 'bg-green-500', '2way': 'bg-blue-500', uc: 'bg-sky-600',
  tollfree: 'bg-teal-500', uifn: 'bg-indigo-500',
};
const PRODUCTS = [
  { label: 'Cloud PBX', to: '/products/cloud-pbx' },
  { label: 'SIP Trunking', to: '/products/sip-trunking' },
  { label: 'Microsoft Teams calling', to: '/products/teams-phone-system' },
  { label: 'AI Receptionist', to: '/products/ai-receptionist' },
];

const safeDecode = (s) => {
  try { return decodeURIComponent(s); } catch (e) { return s; }
};

/** Regulation rows that apply to number kinds offered in the country, labelled in plain words. */
const regulationRows = (data, kinds) => (data?.rules || [])
  .map((r) => ({ ...r, offered: (r.kinds || []).filter((k) => kinds.includes(k)) }))
  .filter((r) => r.offered.length);
import { useLeadGuard } from '../../components/LeadGuard';

const CountryDetails = () => {
  const { country } = useParams();
  const { guard, guardFields, resetGuard } = useLeadGuard();
  const { toast } = useToast();
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  // The answer for the country in the URL: { key, status: ok | missing | error, data }. A result for another
  // country (just after navigating) counts as still loading.
  const [result, setResult] = useState({ key: null });
  const [allCountries, setAllCountries] = useState([]);

  const decodedCountry = safeDecode(country);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const current = result.key === decodedCountry ? result : { status: 'loading' };
  const { status } = current;
  const data = current.data || null;

  useEffect(() => {
    let live = true;
    publicCoverageApi.country(decodedCountry)
      .then((d) => { if (live) setResult({ key: decodedCountry, status: 'ok', data: d }); })
      .catch((error) => {
        if (live) setResult({ key: decodedCountry, status: error?.response?.status === 404 ? 'missing' : 'error' });
      });
    return () => { live = false; };
  }, [decodedCountry]);

  useEffect(() => {
    publicCoverageApi.list().then((r) => setAllCountries((r.countries || []).map((c) => c.country))).catch(() => {});
  }, []);

  // While loading, title and canonical come from the URL; the data then gives the one public spelling.
  const name = data?.country || decodedCountry;
  const dial = dialCodeFor(name);
  const countryCode = flagCode(name);
  const path = countryPath(name);
  const kinds = kindsOf(data);
  const faqs = status === 'ok' ? countryFaqs(name, data, dial) : [];
  const seo = (
    <Seo
      title={countryTitle(name, dial)}
      description={status === 'ok' ? countryDescription(name, data, dial)
        : `Virtual phone numbers in ${name}${dial ? ` (${dial})` : ''} from VOIP2: number types, documents needed and how to order.`}
      path={path}
      breadcrumbs={[{ name: 'Coverage', path: '/coverage' }, { name, path }]}
      jsonLd={faqs.length ? faqLd(faqs) : undefined}
    />
  );

  if (status === 'missing') return <NotFound />;

  // Other spellings ('Bosnia & Herzegovina', 'Uruquay'...) redirect to the canonical country URL.
  if (status === 'ok' && data.country && data.country !== decodedCountry) {
    return <Navigate to={path} replace />;
  }

  const handleInquiry = (productType) => {
    setSelectedProduct({ country: name, productType });
    setInquiryForm({ name: '', email: '', phone: '', company: '', message: `I'm interested in a ${productType} number for ${name}` });
    setInquiryOpen(true);
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...guardFields(),
          name: inquiryForm.name,
          email: inquiryForm.email,
          phone: inquiryForm.phone,
          company: inquiryForm.company,
          country: name,
          service_interest: selectedProduct?.productType,
          message: inquiryForm.message,
          source: 'website',
        }),
      });
      resetGuard();
      if (!response.ok) throw new Error('Failed to submit inquiry');
      toast({ title: 'Inquiry Sent!', description: 'Our team will contact you within 24 hours.' });
      setInquiryOpen(false);
      setInquiryForm({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to submit inquiry. Please try again.', variant: 'destructive' });
    }
  };

  if (status !== 'ok') {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4">
        {seo}
        <div className="max-w-7xl mx-auto text-center">
          {status === 'loading' ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading {name} number information...</p>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold mb-4" style={{ color: NAVY }}>{name} Phone Numbers</h1>
              <p className="text-gray-600 mb-6">We could not load the {name} coverage just now. Please try again in a moment.</p>
              <Link to="/coverage"><Button>Back to Coverage</Button></Link>
            </>
          )}
        </div>
      </div>
    );
  }

  const times = leadTimes(data);
  const docs = allDocuments(data);
  const regs = regulationRows(data, kinds);
  const twoWay = kinds.includes('2way') || kinds.includes('uc');
  const typeNames = listJoin(kinds.map((k) => (k === 'uc' || k === 'uifn' ? KIND_INFO[k].name : KIND_INFO[k].name.toLowerCase())));
  const leadValues = [...new Set(Object.values(times))];
  const typePages = [...new Map(kinds.map((k) => [KIND_INFO[k].page, KIND_INFO[k]])).values()];
  const idx = allCountries.indexOf(name);
  const neighbours = idx < 0 ? [] : [...allCountries.slice(Math.max(0, idx - 3), idx), ...allCountries.slice(idx + 1, idx + 4)];

  const steps = [
    { title: 'Choose your number', body: `Pick from ${typeNames} numbers in ${name} and tell us how many you need.` },
    {
      title: 'Verify your company',
      body: docs.length
        ? `${name} asks for ${docs.length === 1 ? 'one document' : `${docs.length} documents`} (listed below). You upload them once through company verification (KYC) in the customer portal.`
        : `No documents are listed for ${name} at the moment; we confirm when you order.`,
    },
    {
      title: 'Activation',
      body: leadValues.length
        ? `Numbers are usually live ${leadValues.length === 1 && leadValues[0] === 'Immediate' ? 'immediately' : `in ${listJoin(leadValues)}`} once documents are approved.`
        : 'We confirm the activation time when you order.',
    },
    { title: 'Route your calls', body: 'Send calls to the VOIP2 Cloud PBX, a SIP trunk to your own PBX, a mobile or the AI Receptionist, and change routing at any time.' },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      {seo}
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="px-4 sm:px-6 lg:px-8 mb-8">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-2 text-sm text-gray-600">
          <Link to="/" className="hover:text-[#0a1f2e] underline-offset-2 hover:underline">Home</Link>
          <span aria-hidden="true">/</span>
          <Link to="/coverage" className="hover:text-[#0a1f2e] underline-offset-2 hover:underline">Coverage</Link>
          <span aria-hidden="true">/</span>
          <span className="text-gray-900 font-medium">{name}</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16 bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] py-16">
        <div className="max-w-7xl mx-auto">
          <Link to="/coverage" className="inline-flex items-center text-[#c8ff00] hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Coverage
          </Link>
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
            {countryCode && (
              <img
                src={`https://flagcdn.com/w160/${countryCode}.png`}
                alt={`${name} flag`}
                width="128" height="96"
                className="w-32 h-24 rounded-lg shadow-2xl border-4 border-white object-cover"
              />
            )}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4">{name} Phone Numbers</h1>
              <p className="text-xl text-gray-300 max-w-3xl">
                Virtual {typeNames} numbers in {name}{dial ? `, dialling code ${dial}` : ''}, routed to your phone system
                wherever your team works.
              </p>
              <div className="flex flex-wrap gap-3 mt-6 justify-center md:justify-start">
                {kinds.map((k) => (
                  <Badge key={k} className={`${BADGE_COLOURS[k]} text-white px-4 py-2`}>{KIND_INFO[k].name}</Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Number types */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-3" style={{ color: NAVY }}>Number types available in {name}</h2>
          <p className="text-gray-600 mb-8 max-w-3xl">
            {kinds.length === 1 ? 'One number type is' : `${kinds.length} number types are`} currently on offer in {name}.
            Every number can be routed to your Cloud PBX, SIP trunk or another destination from the VOIP2 portal.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {kinds.map((k) => (
              <Card key={k} className="p-6 border-2 border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col">
                {k === 'national' || k === 'uifn' ? <Globe className="w-10 h-10 mb-4" style={{ color: NAVY }} /> : <Phone className="w-10 h-10 mb-4" style={{ color: NAVY }} />}
                <h3 className="text-2xl font-bold mb-2" style={{ color: NAVY }}>{KIND_INFO[k].name} numbers</h3>
                <p className="text-gray-700 mb-4">{KIND_INFO[k].about(name)}</p>
                {times[k] && (
                  <p className="text-sm text-gray-600 mb-6 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Activation: {times[k] === 'Immediate' ? 'immediate' : times[k]}
                  </p>
                )}
                <div className="mt-auto flex flex-col gap-3">
                  <a
                    href={orderUrl(name, k === 'uc' ? '2way' : k)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c8ff00] focus-visible:ring-offset-2"
                    style={{ backgroundColor: NAVY }}
                  >
                    Order a {KIND_INFO[k].name} number <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                  <Button
                    style={{ backgroundColor: '#c8ff00', color: NAVY }}
                    className="w-full hover:opacity-90"
                    onClick={() => handleInquiry(KIND_INFO[k].name)}
                  >
                    Ask about {KIND_INFO[k].name} numbers
                  </Button>
                  <Link to={KIND_INFO[k].page} className="text-sm font-medium underline underline-offset-2 text-center" style={{ color: NAVY }}>
                    About {KIND_INFO[k].plural}
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How to order */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16 bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-8" style={{ color: NAVY }}>How to get a phone number in {name}</h2>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <li key={s.title} className="bg-white rounded-xl p-6 border border-gray-100">
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-full font-bold mb-4" style={{ backgroundColor: '#c8ff00', color: NAVY }}>{i + 1}</span>
                <h3 className="text-lg font-bold mb-2" style={{ color: NAVY }}>{s.title}</h3>
                <p className="text-gray-600">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Outgoing calls */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <Card className="p-8 flex flex-col md:flex-row gap-6 items-start">
            {data.outbound_available ? <PhoneOutgoing className="w-10 h-10 flex-shrink-0" style={{ color: NAVY }} /> : <PhoneIncoming className="w-10 h-10 flex-shrink-0" style={{ color: NAVY }} />}
            <div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: NAVY }}>Outgoing calls from {name} numbers</h2>
              <p className="text-gray-700">
                {data.outbound_available
                  ? `Outgoing calls are available: ${kinds.includes('2way') && kinds.includes('uc') ? '2-way and UC numbers' : kinds.includes('uc') ? 'UC numbers' : '2-way numbers'} in ${name} can call out as well as receive calls, so the people you call see your ${name} number. Call rates are shown in the customer portal when you order.`
                  : twoWay
                    ? `2-way numbers are listed in ${name}, but outgoing call rates are not yet available. Contact us and we will confirm before you order.`
                    : `The ${name} numbers we offer receive calls. 2-way numbers with outgoing calls are not currently available in ${name}.`}
              </p>
              {!data.outbound_available && (
                <p className="text-gray-600 mt-2">
                  For outgoing calls from your own phone system, see <Link to="/products/sip-trunking" className="underline underline-offset-2">SIP Trunking</Link>.
                </p>
              )}
            </div>
          </Card>
        </div>
      </section>

      {/* Regulations summary */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-8 h-8" style={{ color: NAVY }} />
            <h2 className="text-3xl font-bold" style={{ color: NAVY }}>{name} number regulations and KYC</h2>
          </div>
          <p className="text-gray-600 mb-8 max-w-3xl">
            Phone numbers in {name} come with rules set by the country's regulator. This is what we will ask for; the full
            requirements are on the <Link to={countryPath(name, '/regulatory-requirements')} className="underline underline-offset-2">{name} regulatory requirements</Link> page.
          </p>
          {regs.length ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {regs.map((r, i) => (
                <Card key={i} className="p-6">
                  <h3 className="text-xl font-bold mb-4" style={{ color: NAVY }}>
                    {listJoin(r.offered.map((k) => KIND_INFO[k].name))} numbers
                  </h3>
                  <dl className="space-y-3 text-gray-700">
                    {r.address && (
                      <div className="flex gap-3">
                        <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-500" />
                        <div><dt className="font-semibold">Address</dt><dd>{r.address}</dd></div>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <FileCheck className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-500" />
                      <div>
                        <dt className="font-semibold">Documents</dt>
                        <dd>
                          {r.documents?.length
                            ? <ul className="list-disc ml-5">{r.documents.map((d) => <li key={d}>{d}</li>)}</ul>
                            : 'None listed'}
                        </dd>
                      </div>
                    </div>
                    {r.rules?.length > 0 && (
                      <div className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-500" />
                        <div><dt className="font-semibold">Rules</dt><dd><ul className="list-disc ml-5">{r.rules.map((x) => <li key={x}>{x}</li>)}</ul></dd></div>
                      </div>
                    )}
                  </dl>
                  {r.to_follow && <p className="text-sm text-gray-500 mt-4">Some requirements are still to be confirmed; we tell you exactly what is needed when you order.</p>}
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-gray-700">No specific requirements are listed for {name} at the moment. We confirm anything that is needed when you order.</p>
          )}
          <div className="mt-8">
            <Link to={countryPath(name, '/regulatory-requirements')}>
              <Button size="lg" style={{ backgroundColor: NAVY, color: '#fff' }} className="hover:opacity-90 font-semibold">
                Full {name} requirements <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16 bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8" style={{ color: NAVY }}>{name} phone numbers: questions and answers</h2>
          <div className="space-y-4">
            {faqs.map((f) => (
              <div key={f.q} className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-bold mb-2" style={{ color: NAVY }}>{f.q}</h3>
                <p className="text-gray-700">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-8" style={{ color: NAVY }}>Related pages</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-bold mb-3" style={{ color: NAVY }}>Number types</h3>
              <ul className="space-y-2">
                {typePages.map((t) => (
                  <li key={t.page}><Link to={t.page} className="text-gray-700 underline underline-offset-2 hover:text-[#0a1f2e]">{t.name === 'UC' ? '2-Way and UC' : t.name} numbers</Link></li>
                ))}
                <li><Link to="/coverage" className="text-gray-700 underline underline-offset-2 hover:text-[#0a1f2e]">All countries</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-3" style={{ color: NAVY }}>Use your {name} number with</h3>
              <ul className="space-y-2">
                {PRODUCTS.map((p) => (
                  <li key={p.to}><Link to={p.to} className="text-gray-700 underline underline-offset-2 hover:text-[#0a1f2e]">{p.label}</Link></li>
                ))}
              </ul>
            </div>
            {neighbours.length > 0 && (
              <div>
                <h3 className="text-lg font-bold mb-3" style={{ color: NAVY }}>More countries</h3>
                <ul className="space-y-2">
                  {neighbours.map((c) => (
                    <li key={c}><Link to={countryPath(c)} className="text-gray-700 underline underline-offset-2 hover:text-[#0a1f2e]">{c} phone numbers</Link></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Inquiry Dialog */}
      <Dialog open={inquiryOpen} onOpenChange={setInquiryOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Number Inquiry</DialogTitle>
            <DialogDescription>
              {selectedProduct && (
                <span>Request a <strong>{selectedProduct.productType}</strong> number for <strong>{selectedProduct.country}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInquirySubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <Input required placeholder="John Smith" value={inquiryForm.name} onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <Input type="email" required placeholder="john@company.com" value={inquiryForm.email} onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <Input type="tel" placeholder="+44 204 575 2222" value={inquiryForm.phone} onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <Input placeholder="Your Company Name" value={inquiryForm.company} onChange={(e) => setInquiryForm({ ...inquiryForm, company: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <Textarea required rows={4} value={inquiryForm.message} onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })} />
            </div>
            {guard}
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setInquiryOpen(false)}>Cancel</Button>
              <Button type="submit" style={{ backgroundColor: '#c8ff00', color: NAVY }}>
                <Mail className="w-4 h-4 mr-2" />Send Inquiry
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CountryDetails;
